import React, { useEffect, useMemo, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import usePrivateLink from "../config/usePrivateLink";
import { useLocation, useNavigate } from "react-router-dom";
import {
  distributionKitDetails,
  distributionKitItemList,
  distributionKitItemAdd,
  distributionKitItemUpdate,
  distributionKitItemDelete,
} from "../../api/DistributionKit";
import Swal from "sweetalert2";

const GST_OPTIONS = [0, 5, 12, 18, 28];

const emptyForm = {
  id: null,
  name: "",
  description: "",
  quantity: "",
  is_free: false,
  price_without_gst: "",
  price_with_gst: "",
  gst: "",
  discount_amount: "",
};

export default function DistributionKitItems() {
  usePrivateLink();

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const kitId = searchParams.get("kit_id");

  const [loading, setLoading] = useState(false);
  const [kit, setKit] = useState({});
  const [items, setItems] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const getKit = async () => {
    if (!kitId) return;

    try {
      const response = await distributionKitDetails(kitId);

      if (response.data.success) {
        setKit(response?.data?.data || {});
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
    }
  };

  const getItems = async () => {
    if (!kitId) return;

    setLoading(true);

    try {
      const response = await distributionKitItemList(kitId);

      if (response.data.success) {
        setItems(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!kitId) {
      navigate("/distribution/kits");
      return;
    }

    getKit();
    getItems();
  }, [kitId]);

  const stats = useMemo(() => {
    const total = items.length;

    const active = items.filter((i) => Number(i.status) === 1).length;

    const totalQuantity = items.reduce(
      (sum, i) => sum + Number(i.quantity || 0),
      0,
    );

    return {
      total,
      active,
      totalQuantity,
    };
  }, [items]);

  const openAddModal = () => {
    setIsEdit(false);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setIsEdit(true);

    const isFree = !!item.is_free;

    const priceWithoutGst = isFree ? 0 : Number(item.price_without_gst || 0);

    const gst = isFree ? 0 : Number(item.gst || 0);

    const priceWithGst = isFree
      ? 0
      : (priceWithoutGst + (priceWithoutGst * gst) / 100).toFixed(2);

    setFormData({
      id: item.id,
      name: item.name || "",
      description: item.description || "",
      quantity: item.quantity ?? "",
      is_free: isFree,
      price_without_gst: priceWithoutGst,
      price_with_gst: priceWithGst,
      gst: gst,
      discount_amount: isFree ? 0 : (item.discount_amount ?? ""),
    });

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(emptyForm);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "is_free") {
        if (value === true) {
          next.gst = 0;
          next.price_without_gst = 0;
          next.price_with_gst = 0;
          next.discount_amount = 0;
        } else {
          next.gst = "";
          next.price_without_gst = "";
          next.price_with_gst = "";
          next.discount_amount = "";
        }
      }

      // Calculate Price With GST automatically
      if (field === "price_without_gst" || field === "gst") {
        const priceWithoutGst =
          field === "price_without_gst"
            ? Number(value || 0)
            : Number(prev.price_without_gst || 0);

        const gst =
          field === "gst" ? Number(value || 0) : Number(prev.gst || 0);

        next.price_with_gst = (
          priceWithoutGst +
          (priceWithoutGst * gst) / 100
        ).toFixed(2);
      }

      return next;
    });
  };

  const previewFinalPrice = formData.is_free
    ? 0
    : Math.max(
        0,
        Number(formData.price_with_gst || 0) -
          Number(formData.discount_amount || 0),
      );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        gst: formData.is_free ? 0 : formData.gst,
        discount_amount: formData.is_free
          ? 0
          : Number(formData.discount_amount || 0),
      };

      delete payload.id;

      const response = isEdit
        ? await distributionKitItemUpdate(kitId, formData.id, payload)
        : await distributionKitItemAdd(kitId, payload);

      if (response.data.success) {
        SweetAlertShow("success", "Successfully", response.data.message);

        closeModal();
        getItems();
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: "Delete Item?",
      text: `Are you sure you want to delete "${item.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);

    try {
      const response = await distributionKitItemDelete(kitId, item.id);

      if (response.data.success) {
        SweetAlertShow(
          "success",
          "Successfully",
          response.data.message || "Item deleted successfully.",
        );

        getItems();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response.data.message || "Unable to delete item.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid flex-grow-1 container-p-y">
              {/* =====================================================
                  PAGE / KIT HEADER
              ====================================================== */}
              <div className="card border-0 shadow-sm mb-4 overflow-hidden">
                <div className="card-body p-4">
                  {/* Top Navigation */}
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <button
                      type="button"
                      className="btn btn-label-secondary d-flex align-items-center"
                      onClick={() => navigate("/distribution/kits")}
                    >
                      <i className="ri-arrow-left-line me-1" />
                      Back to Kits
                    </button>

                    <span className="text-muted small">Kit #{kitId}</span>
                  </div>

                  {/* Kit Information */}
                  <div className="row align-items-center g-4">
                    {/* Kit Details */}
                    <div className="col-xl-6 col-lg-12">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                          style={{
                            width: 58,
                            height: 58,
                            minWidth: 58,
                          }}
                        >
                          <i className="ri-archive-stack-line fs-3 text-primary" />
                        </div>

                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h4 className="mb-0 fw-bold">
                              {kit?.name || "Kit"}
                            </h4>

                            {kit?.is_free ? (
                              <span className="badge bg-label-success rounded-pill">
                                <i className="ri-gift-line me-1" />
                                Free
                              </span>
                            ) : null}

                            {Number(kit?.status) === 1 ? (
                              <span className="badge bg-label-success rounded-pill">
                                Active
                              </span>
                            ) : (
                              <span className="badge bg-label-secondary rounded-pill">
                                Inactive
                              </span>
                            )}
                          </div>

                          <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                            <span className="text-muted small">
                              <i className="ri-box-3-line me-1" />
                              {stats.total} item
                              {stats.total !== 1 ? "s" : ""}
                            </span>

                            <span className="text-muted small">
                              <i className="ri-stack-line me-1" />
                              {stats.totalQuantity} total quantity
                            </span>

                            {!kit?.is_free && (
                              <span className="text-muted small">
                                <i className="ri-money-rupee-circle-line me-1" />
                                ₹{kit?.final_price ?? "-"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="col-xl-6 col-lg-12">
                      <div className="row g-3 justify-content-xl-end">
                        {/* Total Items */}
                        <div className="col-sm-4">
                          <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-primary h-100">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-2 bg-primary text-white"
                              style={{
                                width: 40,
                                height: 40,
                                minWidth: 40,
                              }}
                            >
                              <i className="ri-box-3-line fs-5" />
                            </div>

                            <div>
                              <div className="text-muted small">Items</div>

                              <div className="fs-5 fw-bold">{stats.total}</div>
                            </div>
                          </div>
                        </div>

                        {/* Active */}
                        <div className="col-sm-4">
                          <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-success h-100">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-2 bg-success text-white"
                              style={{
                                width: 40,
                                height: 40,
                                minWidth: 40,
                              }}
                            >
                              <i className="ri-checkbox-circle-line fs-5" />
                            </div>

                            <div>
                              <div className="text-muted small">Active</div>

                              <div className="fs-5 fw-bold text-success">
                                {stats.active}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Quantity */}
                        <div className="col-sm-4">
                          <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-info h-100">
                            <div
                              className="d-flex align-items-center justify-content-center rounded-2 bg-info text-white"
                              style={{
                                width: 40,
                                height: 40,
                                minWidth: 40,
                              }}
                            >
                              <i className="ri-stack-line fs-5" />
                            </div>

                            <div>
                              <div className="text-muted small">Total Qty</div>

                              <div className="fs-5 fw-bold text-info">
                                {stats.totalQuantity}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* =====================================================
                  ITEMS LIST
              ====================================================== */}
              <div className="card border-0 shadow-sm">
                {/* Toolbar */}
                <div className="card-header border-bottom px-3 px-md-4 py-3">
                  <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div>
                      <h5 className="mb-1 fw-bold">Kit Items</h5>

                      <p className="text-muted small mb-0">
                        Manage products included in this kit
                      </p>
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center"
                      onClick={openAddModal}
                    >
                      <i className="ri-add-line me-1" />
                      Add Item
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table
                      className="table table-hover align-middle mb-0"
                      style={{ minWidth: 900 }}
                    >
                      <thead>
                        <tr>
                          <th className="ps-4">Item</th>

                          <th>Quantity</th>

                          <th>Price</th>

                          <th>GST</th>

                          <th>Final Price</th>

                          <th>Status</th>

                          <th className="text-end pe-4">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {/* Empty State */}
                        {items?.length === 0 && (
                          <tr>
                            <td colSpan={7}>
                              <div className="text-center py-5">
                                <div
                                  className="d-flex align-items-center justify-content-center rounded-circle bg-label-primary mx-auto mb-3"
                                  style={{
                                    width: 68,
                                    height: 68,
                                  }}
                                >
                                  <i className="ri-box-3-line fs-2 text-primary" />
                                </div>

                                <h6 className="mb-1 fw-semibold">
                                  No items added yet
                                </h6>

                                <p className="text-muted small mb-3">
                                  Add products or items that belong to this
                                  distribution kit.
                                </p>

                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={openAddModal}
                                >
                                  <i className="ri-add-line me-1" />
                                  Add First Item
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Items */}
                        {items?.map((item) => (
                          <tr key={item.id}>
                            {/* Item */}
                            <td className="ps-4">
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                                  style={{
                                    width: 42,
                                    height: 42,
                                    minWidth: 42,
                                  }}
                                >
                                  <i className="ri-box-3-line fs-5 text-primary" />
                                </div>

                                <div style={{ minWidth: 0 }}>
                                  <div
                                    className="fw-semibold text-truncate"
                                    style={{
                                      maxWidth: 280,
                                    }}
                                  >
                                    {item.name}
                                  </div>

                                  <div className="d-flex align-items-center gap-2 mt-1">
                                    {item.is_free ? (
                                      <span className="badge bg-label-success rounded-pill">
                                        <i className="ri-gift-line me-1" />
                                        Free
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Quantity */}
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-semibold">
                                  {item.quantity}
                                </span>
                              </div>
                            </td>

                            {/* Price */}
                            <td>
                              {item.is_free ? (
                                <span className="fw-semibold text-success">
                                  Free
                                </span>
                              ) : (
                                <span className="fw-semibold">
                                  ₹{item.price_with_gst}
                                </span>
                              )}
                            </td>

                            {/* GST */}
                            <td>
                              <span className="badge bg-label-secondary rounded-pill">
                                {item.gst}%
                              </span>
                            </td>

                            {/* Final Price */}
                            <td>
                              {item.is_free ? (
                                <span className="fw-bold text-success">
                                  ₹0.00
                                </span>
                              ) : (
                                <div>
                                  <div className="fw-bold">
                                    ₹{item.final_price}
                                  </div>

                                  {Number(item.discount_amount) > 0 && (
                                    <div className="text-success small mt-1">
                                      <i className="ri-price-tag-3-line me-1" />
                                      Discount ₹{item.discount_amount}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>

                            {/* Status */}
                            <td>
                              {Number(item.status) === 1 ? (
                                <span className="badge bg-label-success rounded-pill px-3">
                                  <span
                                    className="d-inline-block rounded-circle bg-success me-1"
                                    style={{
                                      width: 6,
                                      height: 6,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  Active
                                </span>
                              ) : (
                                <span className="badge bg-label-secondary rounded-pill px-3">
                                  <span
                                    className="d-inline-block rounded-circle bg-secondary me-1"
                                    style={{
                                      width: 6,
                                      height: 6,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  Inactive
                                </span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="text-end pe-4">
                              <div className="d-inline-flex align-items-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-label-warning"
                                  onClick={() => openEditModal(item)}
                                  title="Edit Item"
                                >
                                  <i className="ri-edit-line" />
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-label-danger"
                                  onClick={() => handleDelete(item)}
                                  title="Delete Item"
                                >
                                  <i className="ri-delete-bin-line" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer */}
                {items?.length > 0 && (
                  <div className="card-footer border-top py-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="text-muted small">
                        Showing{" "}
                        <strong className="text-body">{items.length}</strong>{" "}
                        item
                        {items.length !== 1 ? "s" : ""}
                      </span>

                      <span className="text-muted small">
                        Total quantity:{" "}
                        <strong className="text-body">
                          {stats.totalQuantity}
                        </strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==========================================================
          ADD / EDIT MODAL
      =========================================================== */}
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg">
                <form onSubmit={handleSubmit}>
                  {/* =================================================
                      MODAL HEADER
                  ================================================== */}
                  <div className="modal-header border-bottom px-4 py-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                        style={{
                          width: 46,
                          height: 46,
                          minWidth: 46,
                        }}
                      >
                        <i className="ri-box-3-line fs-5 text-primary" />
                      </div>

                      <div>
                        <h5 className="modal-title fw-bold mb-0">
                          {isEdit ? "Edit Kit Item" : "Add Kit Item"}
                        </h5>

                        <small className="text-muted">
                          {isEdit
                            ? "Update item information and pricing"
                            : "Add a new item to this distribution kit"}
                        </small>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeModal}
                    />
                  </div>

                  {/* =================================================
                      MODAL BODY
                  ================================================== */}
                  <div className="modal-body p-4">
                    {/* =================================================
                        BASIC INFORMATION
                    ================================================== */}
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-2 bg-label-primary"
                          style={{
                            width: 30,
                            height: 30,
                          }}
                        >
                          <i className="ri-information-line text-primary" />
                        </div>

                        <div>
                          <div className="fw-semibold">Basic Information</div>

                          <div className="text-muted small">
                            Enter item details and quantity
                          </div>
                        </div>
                      </div>

                      <div className="row g-3">
                        {/* Name */}
                        <div className="col-md-8">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Item Name"
                              value={formData.name}
                              onChange={(e) =>
                                handleChange("name", e.target.value)
                              }
                              required
                            />

                            <label>
                              Item Name <sup className="text-danger">*</sup>
                            </label>
                          </div>
                        </div>

                        {/* Quantity */}
                        <div className="col-md-4">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="number"
                              min={0}
                              className="form-control"
                              placeholder="Quantity"
                              value={formData.quantity}
                              onChange={(e) =>
                                handleChange("quantity", e.target.value)
                              }
                              required
                            />

                            <label>
                              Quantity <sup className="text-danger">*</sup>
                            </label>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="col-12">
                          <div className="form-floating form-floating-outline">
                            <textarea
                              className="form-control"
                              style={{
                                minHeight: 90,
                                resize: "vertical",
                              }}
                              placeholder="Description"
                              value={formData.description}
                              onChange={(e) =>
                                handleChange("description", e.target.value)
                              }
                            />

                            <label>Description</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        PRICING SETTINGS
                    ================================================== */}
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-2 bg-label-primary"
                          style={{
                            width: 30,
                            height: 30,
                          }}
                        >
                          <i className="ri-money-rupee-circle-line text-primary" />
                        </div>

                        <div>
                          <div className="fw-semibold">Pricing</div>

                          <div className="text-muted small">
                            Configure GST and item pricing
                          </div>
                        </div>
                      </div>

                      <div className="row g-3">
                        {/* Free */}
                        <div className="col-md-4">
                          <div
                            className={`h-100 d-flex align-items-center px-3 rounded-3 border ${
                              formData.is_free ? "bg-label-success" : ""
                            }`}
                          >
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="item_is_free"
                                checked={formData.is_free}
                                onChange={(e) =>
                                  handleChange("is_free", e.target.checked)
                                }
                              />

                              <label
                                className="form-check-label fw-medium"
                                htmlFor="item_is_free"
                              >
                                <i
                                  className={`${
                                    formData.is_free
                                      ? "ri-gift-line text-success"
                                      : "ri-gift-line text-muted"
                                  } me-1`}
                                />
                                Free Item
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* GST */}
                        {!formData.is_free && (
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={formData.gst}
                                onChange={(e) =>
                                  handleChange("gst", e.target.value)
                                }
                                required
                              >
                                <option value="" disabled>
                                  Select GST
                                </option>

                                {GST_OPTIONS.map((g) => (
                                  <option key={g} value={g}>
                                    {g}%
                                  </option>
                                ))}
                              </select>

                              <label>
                                GST <sup className="text-danger">*</sup>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Price Without GST */}
                        {!formData.is_free && (
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                className="form-control"
                                placeholder="Price without GST"
                                value={formData.price_without_gst}
                                onChange={(e) =>
                                  handleChange(
                                    "price_without_gst",
                                    e.target.value,
                                  )
                                }
                                required
                              />

                              <label>
                                Price Without GST{" "}
                                <sup className="text-danger">*</sup>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* =================================================
                        PRICE DETAILS
                    ================================================== */}
                    {!formData.is_free && (
                      <div>
                        <div className="row g-3">
                          {/* Price With GST */}
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                className="form-control"
                                placeholder="Price with GST"
                                value={formData.price_with_gst}
                                readOnly
                                required
                              />

                              <label>
                                Price With GST{" "}
                                <sup className="text-danger">*</sup>
                              </label>
                            </div>
                          </div>

                          {/* Discount */}
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                className="form-control"
                                placeholder="Discount Amount"
                                value={formData.discount_amount}
                                onChange={(e) =>
                                  handleChange(
                                    "discount_amount",
                                    e.target.value,
                                  )
                                }
                              />

                              <label>Discount Amount</label>
                            </div>
                          </div>

                          {/* Final Price */}
                          <div className="col-md-4">
                            <div className="h-100 rounded-3 border bg-label-primary px-3 py-2 d-flex flex-column justify-content-center">
                              <div className="text-primary small fw-medium">
                                FINAL PRICE
                              </div>

                              <div className="fs-5 fw-bold text-primary mt-1">
                                ₹{previewFinalPrice.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Free Item Notice */}
                    {formData.is_free && (
                      <div className="rounded-3 border border-success bg-label-success p-3">
                        <div className="d-flex align-items-start gap-3">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle bg-success text-white"
                            style={{
                              width: 36,
                              height: 36,
                              minWidth: 36,
                            }}
                          >
                            <i className="ri-gift-line" />
                          </div>

                          <div>
                            <div className="fw-semibold text-success">
                              Free Item
                            </div>

                            <div className="text-muted small mt-1">
                              Pricing values are automatically set to zero
                              because this item is marked as free.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* =================================================
                      FOOTER
                  ================================================== */}
                  <div className="modal-footer border-top px-4 py-3">
                    <button
                      type="button"
                      className="btn btn-label-secondary"
                      onClick={closeModal}
                    >
                      <i className="ri-close-line me-1" />
                      Cancel
                    </button>

                    <button type="submit" className="btn btn-primary px-4">
                      <i className="ri-check-line me-1" />

                      {isEdit ? "Update Item" : "Save Item"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
}
